import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Play, CheckCircle, BarChart3, FileText, Download } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';

// Demo data for the preview - Updated to 4 dimensions, People & Change first
const demoQuestions = [
  {
    id: 1,
    dimension: 'peopleChange',
    text: {
      en: 'To what extent does your HR team see the benefits of adopting AI tools?',
      nl: 'In welke mate ziet uw HR-team de voordelen van het adopteren van AI-tools?'
    }
  },
  {
    id: 2,
    dimension: 'governance',
    text: {
      en: 'To what extent does your organization have a clear AI policy with guidelines for responsible use?',
      nl: 'In welke mate heeft uw organisatie een duidelijk AI-beleid met richtlijnen voor verantwoord gebruik?'
    }
  },
  {
    id: 3,
    dimension: 'strategy',
    text: {
      en: 'To what extent is the purpose for adopting AI within HR clear to all stakeholders?',
      nl: 'In welke mate is het doel van AI-adoptie binnen HR duidelijk voor alle stakeholders?'
    }
  },
  {
    id: 4,
    dimension: 'technologyData',
    text: {
      en: 'How well does your technology infrastructure support AI and is it scalable?',
      nl: 'Hoe goed ondersteunt uw technische infrastructuur AI en is deze schaalbaar?'
    }
  },
];

// 4 dimensions matching the assessment categories - People & Change first
const demoResults = [
  { dimension: 'Mensen & Change', dimension_en: 'People & Change', score: 73 },
  { dimension: 'Governance', dimension_en: 'Governance', score: 58 },
  { dimension: 'Strategie', dimension_en: 'Strategy', score: 72 },
  { dimension: 'Technologie & Data', dimension_en: 'Technology & Data', score: 62 },
];

// Mini Spider Chart Component
const MiniSpiderChart = ({ dimensions, animated, language }) => {
  const size = 280;
  const centerX = size / 2;
  const centerY = size / 2;
  const radius = 80;
  const numAxes = dimensions.length;
  const angleStep = (2 * Math.PI) / numAxes;
  
  const [animatedScores, setAnimatedScores] = useState(dimensions.map(() => 0));
  
  useEffect(() => {
    if (animated) {
      const timer = setTimeout(() => {
        setAnimatedScores(dimensions.map(d => d.score));
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [animated, dimensions]);
  
  const getPoint = (value, index) => {
    const angle = (index * angleStep) - (Math.PI / 2);
    const normalizedValue = (value / 100) * radius;
    return {
      x: centerX + normalizedValue * Math.cos(angle),
      y: centerY + normalizedValue * Math.sin(angle)
    };
  };
  
  const generateGridPolygon = (percentage) => {
    const points = [];
    for (let i = 0; i < numAxes; i++) {
      const angle = (i * angleStep) - (Math.PI / 2);
      const r = radius * percentage;
      points.push({
        x: centerX + r * Math.cos(angle),
        y: centerY + r * Math.sin(angle)
      });
    }
    return points.map(p => `${p.x},${p.y}`).join(' ');
  };
  
  const dataPolygon = animatedScores.map((score, i) => {
    const point = getPoint(score, i);
    return `${point.x},${point.y}`;
  }).join(' ');
  
  const gridLevels = [0.25, 0.5, 0.75, 1.0];
  const avgScore = Math.round(dimensions.reduce((sum, d) => sum + d.score, 0) / dimensions.length);
  
  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full">
      <defs>
        <linearGradient id="previewGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#2563eb" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#2563eb" stopOpacity="0.2" />
        </linearGradient>
      </defs>
      
      {/* Grid */}
      {gridLevels.map((level) => (
        <polygon
          key={`grid-${level}`}
          points={generateGridPolygon(level)}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="1"
        />
      ))}
      
      {/* Axes */}
      {dimensions.map((dim, i) => {
        const angle = (i * angleStep) - (Math.PI / 2);
        const endX = centerX + radius * Math.cos(angle);
        const endY = centerY + radius * Math.sin(angle);
        return (
          <line
            key={`axis-${dim.dimension}`}
            x1={centerX}
            y1={centerY}
            x2={endX}
            y2={endY}
            stroke="#d1d5db"
            strokeWidth="1"
          />
        );
      })}
      
      {/* Data polygon */}
      <polygon
        points={dataPolygon}
        fill="url(#previewGradient)"
        stroke="#2563eb"
        strokeWidth="2"
        className="transition-all duration-1000 ease-out"
      />
      
      {/* Data points */}
      {animatedScores.map((score, i) => {
        const point = getPoint(score, i);
        return (
          <circle
            key={`pt2-${i}`}
            cx={point.x}
            cy={point.y}
            r="4"
            fill="#2563eb"
            stroke="#fff"
            strokeWidth="2"
            className="transition-all duration-1000 ease-out"
          />
        );
      })}
      
      {/* Labels */}
      {dimensions.map((dim, i) => {
        const angle = (i * angleStep) - (Math.PI / 2);
        const labelDistance = radius + 30;
        const labelX = centerX + labelDistance * Math.cos(angle);
        const labelY = centerY + labelDistance * Math.sin(angle);
        
        // Use language-specific dimension name
        const displayName = language === 'nl' ? dim.dimension : (dim.dimension_en || dim.dimension);
        
        return (
          <text
            key={`label-${dim.dimension}`}
            x={labelX}
            y={labelY}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="10"
            fontWeight="500"
            fill="#6b7280"
          >
            {displayName}
          </text>
        );
      })}
      
      {/* Center score */}
      <circle cx={centerX} cy={centerY} r="28" fill="white" stroke="#e5e7eb" strokeWidth="2" />
      <text
        x={centerX}
        y={centerY - 6}
        textAnchor="middle"
        fontSize="8"
        fill="#9ca3af"
      >
        SCORE
      </text>
      <text
        x={centerX}
        y={centerY + 10}
        textAnchor="middle"
        fontSize="18"
        fontWeight="700"
        fill="#374151"
      >
        {avgScore}%
      </text>
    </svg>
  );
};

// Question Preview Card Component
const QuestionPreviewCard = ({ question, index, isVisible, sliderValue, t, language }) => {
  const [value, setValue] = useState(3);
  const [animated, setAnimated] = useState(false);
  
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        setAnimated(true);
        // Animate slider to target value
        const interval = setInterval(() => {
          setValue(prev => {
            if (prev < sliderValue) return prev + 1;
            clearInterval(interval);
            return sliderValue;
          });
        }, 200);
        return () => clearInterval(interval);
      }, index * 200);
      return () => clearTimeout(timer);
    }
  }, [isVisible, sliderValue, index]);
  
  return (
    <div 
      className={`bg-white rounded-xl shadow-lg p-5 transition-all duration-700 ${
        animated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 150}ms` }}
    >
      <div className="flex items-center gap-2 mb-3">
        <span className="px-2 py-1 bg-blue-100 text-blue-600 text-xs font-medium rounded-full">
          {t(question.dimension)}
        </span>
      </div>
      <p className="text-gray-800 text-sm font-medium mb-4 line-clamp-2">
        {question.text[language]}
      </p>
      
      {/* Score labels */}
      <div className="flex justify-between text-xs text-gray-400 mb-2">
        <span>{t('notAtAll')}</span>
        <span>{t('completely')}</span>
      </div>
      
      {/* Slider visualization */}
      <div className="relative h-3 rounded-full bg-gradient-to-r from-red-400 via-yellow-400 to-green-400 mb-2">
        <div 
          className="absolute top-1/2 -translate-y-1/2 w-5 h-5 bg-white border-2 border-blue-500 rounded-full shadow-md transition-all duration-500"
          style={{ left: `calc(${((value - 1) / 4) * 100}% - 10px)` }}
        />
      </div>
      
      {/* Score display */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-500">{t('currentScore')}:</span>
        <span className="font-bold text-blue-500">{value}/5</span>
      </div>
    </div>
  );
};

// PDF Preview Component
const PDFPreview = ({ isVisible, t }) => {
  return (
    <div 
      className={`bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      {/* PDF Header */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4 flex items-center gap-3">
        <FileText className="h-6 w-6 text-white" />
        <div>
          <h4 className="text-white font-semibold">{t('pdfPreview')}</h4>
          <p className="text-blue-100 text-xs">AI for HR Maturity Report</p>
        </div>
      </div>
      
      {/* PDF Body Preview */}
      <div className="p-4 space-y-3">
        {/* Mini header */}
        <div className="flex items-center gap-2 pb-2 border-b">
          <img src="/humanistix-ai-logo.png" alt="Logo" className="h-5 w-auto" />
          <span className="text-xs text-gray-400">Quickscan Report</span>
        </div>
        
        {/* Score preview */}
        <div className="bg-blue-50 rounded-lg p-3 text-center">
          <p className="text-xs text-gray-500 mb-1">{t('overallScore')}</p>
          <p className="text-2xl font-bold text-blue-500">66%</p>
          <p className="text-xs text-green-600">Developing</p>
        </div>
        
        {/* Mini chart placeholder */}
        <div className="h-20 bg-gray-50 rounded-lg flex items-center justify-center">
          <div className="w-16 h-16 border-2 border-blue-300 rounded-full flex items-center justify-center">
            <BarChart3 className="h-6 w-6 text-blue-400" />
          </div>
        </div>
        
        {/* Sections preview */}
        <div className="space-y-2">
          <div className="h-2 bg-gray-100 rounded w-full"></div>
          <div className="h-2 bg-gray-100 rounded w-3/4"></div>
          <div className="h-2 bg-gray-100 rounded w-5/6"></div>
        </div>
        
        {/* Recommendations preview */}
        <div className="pt-2 border-t">
          <p className="text-xs font-medium text-gray-700 mb-2">{t('recommendedActions')}</p>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-3 w-3 text-green-500" />
              <div className="h-2 bg-gray-100 rounded flex-1"></div>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-3 w-3 text-green-500" />
              <div className="h-2 bg-gray-100 rounded flex-1"></div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Download indicator */}
      <div className="bg-gray-50 px-4 py-3 flex items-center justify-between">
        <span className="text-xs text-gray-500">PDF • 8 pages</span>
        <div className="flex items-center gap-1 text-blue-500 text-xs font-medium">
          <Download className="h-3 w-3" />
          <span>{t('downloadSample')}</span>
        </div>
      </div>
    </div>
  );
};

const AssessmentPreview = () => {
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('assessment'); // 'assessment', 'results', or 'pdf'
  const { t, language } = useLanguage();
  
  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );
    
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    
    return () => observer.disconnect();
  }, []);
  
  const handleStartAssessment = () => {
    navigate('/quickscan/start');
  };
  
  return (
    <section 
      ref={sectionRef}
      className="py-16 md:py-24 bg-gradient-to-b from-gray-50 to-white overflow-hidden"
      data-testid="assessment-preview-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div 
          className={`text-center mb-12 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-600 rounded-full text-sm font-medium mb-4">
            <Play className="h-4 w-4" />
            {t('seeHowItWorks')}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('experienceAssessment')}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t('experienceSubtitle')}
          </p>
        </div>
        
        {/* Tab Switcher */}
        <div 
          className={`flex justify-center mb-8 transition-all duration-700 delay-100 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex bg-gray-100 rounded-full p-1">
            <button
              onClick={() => setActiveTab('assessment')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                activeTab === 'assessment' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="preview-tab-assessment"
            >
              <span className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                {t('assessmentTab')}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('results')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                activeTab === 'results' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="preview-tab-results"
            >
              <span className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                {t('resultsTab')}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('pdf')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                activeTab === 'pdf' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="preview-tab-pdf"
            >
              <span className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                PDF
              </span>
            </button>
          </div>
        </div>
        
        {/* Preview Content */}
        <div 
          className={`relative transition-all duration-700 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Assessment Preview */}
          <div 
            className={`transition-all duration-500 ${
              activeTab === 'assessment' ? 'opacity-100 visible' : 'opacity-0 invisible absolute inset-0'
            }`}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
              {demoQuestions.map((question, index) => (
                <QuestionPreviewCard
                  key={question.id}
                  question={question}
                  index={index}
                  isVisible={isVisible && activeTab === 'assessment'}
                  sliderValue={[4, 4, 3, 5][index]}
                  t={t}
                  language={language}
                />
              ))}
            </div>
            
            {/* Progress indicator */}
            <div className="max-w-md mx-auto mt-8">
              <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
                <span>{t('assessmentProgress')}</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full bg-gradient-to-r from-blue-400 to-blue-500 rounded-full transition-all duration-1000 ${
                    isVisible ? 'w-[10%]' : 'w-0'
                  }`}
                />
              </div>
            </div>
          </div>
          
          {/* Results Preview */}
          <div 
            className={`transition-all duration-500 ${
              activeTab === 'results' ? 'opacity-100 visible' : 'opacity-0 invisible absolute inset-0'
            }`}
          >
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                {/* Spider Chart */}
                <div 
                  className={`bg-white rounded-2xl shadow-lg p-6 transition-all duration-700 ${
                    isVisible && activeTab === 'results' ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
                  }`}
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
                    {t('yourAIMaturityProfile')}
                  </h3>
                  <div className="w-full max-w-[280px] mx-auto">
                    <MiniSpiderChart 
                      dimensions={demoResults} 
                      animated={isVisible && activeTab === 'results'}
                      language={language}
                    />
                  </div>
                </div>
                
                {/* Score Cards */}
                <div className="space-y-3">
                  {demoResults.map((dim, index) => {
                    const displayName = language === 'nl' ? dim.dimension : (dim.dimension_en || dim.dimension);
                    return (
                    <div
                      key={dim.dimension}
                      className={`bg-white rounded-xl shadow-sm p-4 flex items-center justify-between transition-all duration-500 ${
                        isVisible && activeTab === 'results' 
                          ? 'opacity-100 translate-x-0' 
                          : 'opacity-0 translate-x-8'
                      }`}
                      style={{ transitionDelay: `${index * 100}ms` }}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${
                          dim.score >= 70 ? 'bg-green-500' :
                          dim.score >= 50 ? 'bg-blue-500' :
                          'bg-red-500'
                        }`} />
                        <span className="font-medium text-gray-700">{displayName}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all duration-1000 ${
                              dim.score >= 70 ? 'bg-green-500' :
                              dim.score >= 50 ? 'bg-blue-500' :
                              'bg-red-500'
                            }`}
                            style={{ 
                              width: isVisible && activeTab === 'results' ? `${dim.score}%` : '0%',
                              transitionDelay: `${index * 100 + 300}ms`
                            }}
                          />
                        </div>
                        <span className={`font-bold text-sm w-10 text-right ${
                          dim.score >= 70 ? 'text-green-600' :
                          dim.score >= 50 ? 'text-blue-600' :
                          'text-red-600'
                        }`}>
                          {dim.score}%
                        </span>
                      </div>
                    </div>
                  )})}
                  
                  {/* Overall Score */}
                  <div 
                    className={`bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-4 text-white transition-all duration-700 ${
                      isVisible && activeTab === 'results' 
                        ? 'opacity-100 translate-y-0' 
                        : 'opacity-0 translate-y-4'
                    }`}
                    style={{ transitionDelay: '600ms' }}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{t('overallScore')}</span>
                      <span className="text-2xl font-bold">
                        {Math.round(demoResults.reduce((sum, d) => sum + d.score, 0) / demoResults.length)}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* PDF Preview */}
          <div 
            className={`transition-all duration-500 ${
              activeTab === 'pdf' ? 'opacity-100 visible' : 'opacity-0 invisible absolute inset-0'
            }`}
          >
            <div className="max-w-sm mx-auto">
              <PDFPreview isVisible={isVisible && activeTab === 'pdf'} t={t} />
            </div>
          </div>
        </div>
        
        {/* CTA Button */}
        <div 
          className={`text-center mt-12 transition-all duration-700 delay-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <Button
            onClick={handleStartAssessment}
            size="lg"
            className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-6 text-lg font-semibold rounded-full shadow-lg hover:shadow-xl transition-all"
            data-testid="preview-start-assessment-btn"
          >
            {t('startYourFreeAssessment')}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <p className="text-gray-500 text-sm mt-4">
            {t('takesOnly')}
          </p>
        </div>
      </div>
    </section>
  );
};

export default AssessmentPreview;
